import { useEffect, useRef, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { Document, Page, pdfjs } from 'react-pdf';
import PropTypes from 'prop-types';
import { Box, IconButton } from '@mui/material';
import { useWindowSize } from 'react-use';
import { LoadingSmall } from 'components/Loading';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

// Ensure that the pdf.worker.mjs file exists in the public directory.
// If it is missing, run the `copy-pdf-worker.js` script to copy it there.
pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.mjs`;

function PDFViewer({
  value,
  file,
  handleChangePdfUrl,
}: {
  value?: React.JSX.Element;
  file?: string;
  handleChangePdfUrl: (value: string) => void;
}) {
  const [pageWidth, setPageWidth] = useState(0);
  const [previousRenderValue, setPreviousRenderValue] = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const documentWrapperRef = useRef<HTMLDivElement>(null);
  const { width: windowWidth } = useWindowSize();

  const [renderUrl, setRenderUrl] = useState<string | null>(null);
  const [renderLoading, setRenderLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRenderLoading(true);

    (async () => {
      try {
        if (file) {
          if (!cancelled) {
            setRenderUrl(file);
            setRenderLoading(false);
          }
          return;
        }
        if (!value) {
          if (!cancelled) {
            setRenderUrl(null);
            setRenderLoading(false);
          }
          return;
        }
        const blob = await pdf(value).toBlob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setRenderUrl(url);
        setRenderLoading(false);
      } catch {
        if (!cancelled) setRenderLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [value, file]);

  useEffect(() => {
    handleChangePdfUrl(renderUrl as string);
  }, [renderUrl]);

  useEffect(() => {
    // Update the page width based on the current container width
    if (documentWrapperRef.current) {
      setPageWidth(documentWrapperRef.current.offsetWidth);
    }
  }, [windowWidth]); // Re-run when window width changes

  const isFirstRendering = !previousRenderValue;
  const isLatestValueRendered = previousRenderValue === renderUrl;
  const isBusy = renderLoading || !isLatestValueRendered;
  const shouldShowPreviousDocument = !isFirstRendering && isBusy;
  const shouldShowLoadingSpinner = !renderUrl || (isFirstRendering && renderLoading);

  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, numPages));
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  return (
    <>
      <Box
        sx={{
          boxShadow: (theme) => (shouldShowLoadingSpinner ? 'none' : theme.shadows[3]),
          '& .react-pdf__Document': {
            '&.previous-document': {
              '& canvas': { opacity: 0.5 },
            },
            '&.rendering-document': {
              position: 'absolute',
              boxShadow: 'none',
            },
          },
        }}
        ref={documentWrapperRef}
      >
        {shouldShowLoadingSpinner ? (
          <LoadingSmall />
        ) : (
          <Box>
            {shouldShowPreviousDocument && previousRenderValue && !isLatestValueRendered ? (
              <Document
                key={previousRenderValue}
                className="previous-document"
                file={previousRenderValue}
                loading={null}
              >
                <Page pageNumber={1} loading={null} width={pageWidth} />
              </Document>
            ) : null}

            <Document
              key={renderUrl}
              className={shouldShowPreviousDocument ? 'rendering-document' : null}
              file={renderUrl}
              loading={null}
              onLoadSuccess={onDocumentLoadSuccess}
            >
              <Page
                pageNumber={currentPage}
                width={pageWidth}
                onRenderSuccess={() => setPreviousRenderValue(renderUrl as string)}
                loading={null}
              />
            </Document>
          </Box>
        )}
      </Box>

      {/* Pagination controls */}
      {numPages > 0 && (
        <Box mt={2} display="flex" justifyContent="center" alignItems="center" gap={2}>
          <IconButton onClick={handlePrevPage} disabled={currentPage <= 1}>
            <ArrowBackIcon />
          </IconButton>

          <span>
            {currentPage} / {numPages}
          </span>
          <IconButton onClick={handleNextPage} disabled={currentPage >= numPages}>
            <ArrowForwardIcon />
          </IconButton>
        </Box>
      )}
    </>
  );
}

PDFViewer.propTypes = {
  value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  handleChangePdfUrl: PropTypes.func.isRequired,
};

export default PDFViewer;
